import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Bell, Eye, Globe, LogIn, LogOut, Menu, Moon, ShoppingCart, Sun, DollarSign, Palette as ThemeIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useTheme } from '@/context/ThemeContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import { useAdminView } from '@/context/AdminViewContext';
import StoreClosureBanner from '@/components/StoreClosureBanner';
import JwalekLogo from '@/components/JwalekLogo';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

const NAV = [
  { to: '/shop', key: 'nav.shop', fallback: 'Shop' },
  { to: '/designs', key: 'nav.designs', fallback: 'Designs' },
  { to: '/community', key: 'nav.community', fallback: 'Community' },
  { to: '/top-rated', key: 'nav.topRated', fallback: 'Top Rated' },
  { to: '/ai-generator', key: 'nav.aiGenerator', fallback: 'AI Generator' },
  { to: '/contact', key: 'nav.contact', fallback: 'Contact' },
];

// Compact row of preference toggles reused inside the hamburger sheet and the
// user dropdown so signed-in and guest users can reach lang / currency / theme
// from a single "collapsible menu" no matter the viewport size.
const PreferenceToggles = ({ compact }) => {
  const { theme, toggle: toggleTheme } = useTheme();
  const { currency, toggle: toggleCurrency } = useCurrency();
  const { lang, toggle: toggleLang, t } = useLanguage();

  const btn = 'flex-1 h-10 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 jw-panel hover:opacity-80';
  return (
    <div className={`flex items-stretch gap-2 ${compact ? 'px-1 py-2' : ''}`}>
      <button
        onClick={toggleLang}
        data-testid="header-language-toggle-button"
        title={t('nav.language', 'Switch language')}
        aria-label={t('nav.language', 'Switch language')}
        className={btn}
        style={{ color: 'var(--jw-text)' }}
      >
        <Globe className="w-4 h-4" />
        <span>{lang === 'ar' ? 'ع' : 'EN'}</span>
      </button>
      <button
        onClick={toggleCurrency}
        data-testid="header-currency-toggle-button"
        title={t('nav.currency', 'Switch currency')}
        aria-label={t('nav.currency', 'Switch currency')}
        className={btn}
        style={{ color: 'var(--jw-text)' }}
      >
        <DollarSign className="w-4 h-4" />
        <span>{currency}</span>
      </button>
      <button
        onClick={toggleTheme}
        data-testid="header-theme-toggle-button"
        title={t('nav.theme', 'Toggle theme')}
        aria-label={t('nav.theme', 'Toggle theme')}
        className={btn}
        style={{ color: 'var(--jw-text)' }}
      >
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        <span>{theme === 'dark' ? t('nav.light', 'Light') : t('nav.dark', 'Dark')}</span>
      </button>
    </div>
  );
};

const Header = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { lang, t } = useLanguage();
  const { asCustomer, isAdminAsCustomer, toggle: toggleAdminView } = useAdminView();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get('/notifications').then(({ data }) => setNotifs(data)).catch(() => {});
  }, [user]);

  const unread = notifs.filter(n => !n.read).length;

  const handleAdminViewToggle = () => {
    const willBecomeCustomer = !asCustomer;
    toggleAdminView();
    if (willBecomeCustomer) {
      toast.success(t('admin.customerModeToast', "You're now browsing as a customer."));
      navigate('/');
    } else {
      toast.success(t('admin.adminModeToast', 'Back in admin view.'));
      navigate('/admin');
    }
  };

  return (
    <>
      <StoreClosureBanner />
      <header className="sticky top-0 z-40 jw-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-6 min-w-0">
            <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
              <JwalekLogo size={28} />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.fallback.toLowerCase().replace(' ', '-')}-link`}
                  className={({ isActive }) => `px-3 py-2 rounded-md text-sm ${isActive ? 'jw-panel font-medium' : 'hover:opacity-80'}`}
                  style={({ isActive }) => ({ color: isActive ? 'var(--jw-text)' : 'var(--jw-text-muted)' })}
                >
                  {t(n.key, n.fallback)}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Admin: view site as customer (eye icon). Only shown for admins. */}
            {user?.role === 'admin' && (
              <button
                onClick={handleAdminViewToggle}
                data-testid="header-admin-view-toggle-button"
                title={isAdminAsCustomer ? t('admin.viewAsAdmin', 'Back to admin view') : t('admin.viewAsCustomer', 'View site as customer')}
                aria-label={isAdminAsCustomer ? t('admin.viewAsAdmin', 'Back to admin view') : t('admin.viewAsCustomer', 'View site as customer')}
                className={`p-2 rounded-md hover:opacity-80 border ${isAdminAsCustomer ? 'jw-panel border-orange-400/50 text-orange-300' : 'border-transparent'}`}
                style={{ color: isAdminAsCustomer ? undefined : 'var(--jw-text-muted)' }}
              >
                <Eye className="w-4 h-4" />
              </button>
            )}

            {user && (
              <Link
                to="/notifications"
                data-testid="header-notifications-link"
                className="relative p-2 rounded-md hover:opacity-80"
                style={{ color: 'var(--jw-text-muted)' }}
              >
                <Bell className="w-5 h-5" />
                {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full jw-gradient-bg" />}
              </Link>
            )}

            <Link
              to="/cart"
              data-testid="header-cart-link"
              className="relative p-2 rounded-md hover:opacity-80"
              style={{ color: 'var(--jw-text-muted)' }}
            >
              <ShoppingCart className="w-5 h-5" />
              {cart.items?.length > 0 && (
                <Badge
                  className="absolute -top-1 -right-1 h-5 min-w-5 p-0 grid place-items-center jw-btn-primary text-[10px]"
                  data-testid="header-cart-count"
                >
                  {cart.items.reduce((s, i) => s + i.quantity, 0)}
                </Badge>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="header-user-menu-button" className="gap-2 px-1.5 sm:px-2.5">
                    <div className="w-7 h-7 rounded-full jw-gradient-bg grid place-items-center text-xs font-bold text-white">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <span className="hidden lg:inline text-sm">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile-item">
                    {t('nav.profile', 'Profile')}
                  </DropdownMenuItem>
                  {user.role !== 'admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/my-designs')} data-testid="menu-my-designs-item">
                        {t('nav.myDesigns', 'My Designs')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites-item">
                        {t('nav.favorites', 'Favorites')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="menu-orders-item">
                        {t('nav.orders', 'Orders')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin-item">
                      {t('nav.admin', 'Admin Dashboard')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--jw-text-subtle)' }}>
                    {t('nav.preferences', 'Preferences')}
                  </DropdownMenuLabel>
                  <div className="px-2 pb-2" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
                    <PreferenceToggles />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="menu-logout-item">
                    <LogOut className="w-4 h-4 mr-2" /> {t('nav.signOut', 'Sign out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="hidden sm:inline-flex">
                <Button size="sm" variant="secondary" data-testid="header-login-button" className="gap-1">
                  <LogIn className="w-4 h-4" /> {t('nav.signIn', 'Sign in')}
                </Button>
              </Link>
            )}

            {/* Universal collapsible menu — visible on ALL sizes so guests + signed-in
              * users can access language / currency / theme, nav links (mobile) and
              * legal pages from a single place. Keeps the top bar uncluttered. */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="header-mobile-menu-button" aria-label={t('nav.menu', 'Menu')}>
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side={lang === 'ar' ? 'left' : 'right'} className="w-80 flex flex-col">
                <SheetTitle className="sr-only">{t('nav.preferences', 'Preferences')}</SheetTitle>
                <div className="mt-6 flex-1 flex flex-col gap-5 overflow-y-auto">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider mb-2 px-1" style={{ color: 'var(--jw-text-subtle)' }}>
                      {t('nav.preferences', 'Preferences')}
                    </p>
                    <PreferenceToggles />
                  </div>

                  <div className="md:hidden">
                    <p className="text-[10px] uppercase tracking-wider mb-2 px-1" style={{ color: 'var(--jw-text-subtle)' }}>
                      {t('nav.browse', 'Browse')}
                    </p>
                    <div className="flex flex-col gap-1">
                      {NAV.map(n => (
                        <Link
                          key={n.to}
                          to={n.to}
                          onClick={() => setSheetOpen(false)}
                          data-testid={`sheet-nav-${n.fallback.toLowerCase().replace(' ', '-')}-link`}
                          className="p-3 rounded-md hover:jw-panel text-sm"
                          style={{ color: 'var(--jw-text)' }}
                        >
                          {t(n.key, n.fallback)}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wider mb-2 px-1" style={{ color: 'var(--jw-text-subtle)' }}>
                      {t('nav.account', 'Account')}
                    </p>
                    <div className="flex flex-col gap-1">
                      {!user && (
                        <Link
                          to="/login"
                          onClick={() => setSheetOpen(false)}
                          data-testid="sheet-signin-link"
                          className="p-3 rounded-md hover:jw-panel text-sm inline-flex items-center gap-2"
                          style={{ color: 'var(--jw-text)' }}
                        >
                          <LogIn className="w-4 h-4" /> {t('nav.signIn', 'Sign in')}
                        </Link>
                      )}
                      {user && (
                        <>
                          <Link
                            to="/profile"
                            onClick={() => setSheetOpen(false)}
                            className="p-3 rounded-md hover:jw-panel text-sm"
                            style={{ color: 'var(--jw-text)' }}
                          >
                            {t('nav.profile', 'Profile')}
                          </Link>
                          {user.role !== 'admin' && (
                            <>
                              <Link to="/orders" onClick={() => setSheetOpen(false)} className="p-3 rounded-md hover:jw-panel text-sm md:hidden" style={{ color: 'var(--jw-text)' }}>
                                {t('nav.orders', 'Orders')}
                              </Link>
                              <Link to="/favorites" onClick={() => setSheetOpen(false)} className="p-3 rounded-md hover:jw-panel text-sm md:hidden" style={{ color: 'var(--jw-text)' }}>
                                {t('nav.favorites', 'Favorites')}
                              </Link>
                              <Link to="/my-designs" onClick={() => setSheetOpen(false)} className="p-3 rounded-md hover:jw-panel text-sm md:hidden" style={{ color: 'var(--jw-text)' }}>
                                {t('nav.myDesigns', 'My Designs')}
                              </Link>
                            </>
                          )}
                          {user.role === 'admin' && (
                            <Link to="/admin" onClick={() => setSheetOpen(false)} className="p-3 rounded-md hover:jw-panel text-sm" style={{ color: 'var(--jw-text)' }}>
                              {t('nav.admin', 'Admin Dashboard')}
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t" style={{ borderColor: 'var(--jw-border)' }}>
                    <div className="flex gap-4 text-xs px-1" style={{ color: 'var(--jw-text-muted)' }}>
                      <Link to="/legal/terms" onClick={() => setSheetOpen(false)} className="hover:opacity-80">{t('nav.terms', 'Terms')}</Link>
                      <Link to="/legal/privacy" onClick={() => setSheetOpen(false)} className="hover:opacity-80">{t('nav.privacy', 'Privacy')}</Link>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
